import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

import { clampToCellBounds, clampSizeToCellBounds } from '../geometry';
import './QRBlockLayer.css';

// Same inches-as-%-of-usable-area convention as TextBlockLayer/
// ImageBlockLayer (see their header comments) — no fixed px-per-inch
// constant, so this stays correct across the responsive on-screen canvas
// and the true-physical-size @media print page alike.
//
// A QR block is always square: `size` (not width/height) drives both
// dimensions, and the single corner handle resizes both axes together so
// the code never distorts off-square.
export default function QRBlockLayer({
  blocks,
  setBlocks,
  editable,
  selectedIds,
  selectionCount,
  onSelect,
  onGroupDragCandidate,
  showBoundaries,
  usableWidthIn,
  usableHeightIn,
}) {
  const containerRef = useRef(null);
  const [activeId, setActiveId] = useState(null);
  const [resizingId, setResizingId] = useState(null);
  const dragStart = useRef({ mouseX: 0, mouseY: 0, blockX: 0, blockY: 0 });
  const resizeStart = useRef({ mouseX: 0, mouseY: 0, blockSize: 0 });

  const startDrag = (e, id) => {
    if (!editable) return;
    e.stopPropagation();
    // Ctrl/Cmd+click and Shift+click are selection-only gestures (toggle /
    // extend) — they never start a drag.
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      onSelect(id, e);
      return;
    }
    // A plain mousedown on a block that's already part of a multi-selection
    // is ambiguous until the mouse actually moves (click collapses the
    // selection to just this block; click+drag moves the whole selection)
    // — deferred entirely to PrintableMasterGrid's group-drag candidate
    // machinery.
    if (selectionCount > 1 && selectedIds.includes(id)) {
      onGroupDragCandidate(id, e);
      return;
    }
    onSelect(id, e);
    setActiveId(id);
    const block = blocks.find((b) => b.id === id);
    dragStart.current = { mouseX: e.clientX, mouseY: e.clientY, blockX: block.x, blockY: block.y };
  };

  const startResize = (e, id) => {
    if (!editable) return;
    e.stopPropagation();
    onSelect(id, e);
    setResizingId(id);
    const block = blocks.find((b) => b.id === id);
    resizeStart.current = { mouseX: e.clientX, mouseY: e.clientY, blockSize: block.size };
  };

  useEffect(() => {
    if (!editable || (activeId === null && resizingId === null)) return undefined;

    const handleMove = (e) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) return;

      if (activeId !== null) {
        const dxIn = ((e.clientX - dragStart.current.mouseX) / rect.width) * usableWidthIn;
        const dyIn = ((e.clientY - dragStart.current.mouseY) / rect.height) * usableHeightIn;
        setBlocks((prev) =>
          prev.map((b) => {
            if (b.id !== activeId) return b;
            // Clamped every move, not just on drop — same rationale as
            // TextBlockLayer/ImageBlockLayer's drag handlers.
            const { x, y } = clampToCellBounds(
              dragStart.current.blockX + dxIn,
              dragStart.current.blockY + dyIn,
              b.size,
              b.size,
              usableWidthIn,
              usableHeightIn
            );
            return { ...b, x, y };
          })
        );
      }

      if (resizingId !== null) {
        // Average the two axes' drag distance so resizing feels the same
        // regardless of the usable area's aspect ratio, then clamp and
        // collapse back to a single square size.
        const dxIn = ((e.clientX - resizeStart.current.mouseX) / rect.width) * usableWidthIn;
        const dyIn = ((e.clientY - resizeStart.current.mouseY) / rect.height) * usableHeightIn;
        const dIn = (dxIn + dyIn) / 2;
        setBlocks((prev) =>
          prev.map((b) => {
            if (b.id !== resizingId) return b;
            const requested = resizeStart.current.blockSize + dIn;
            const { width, height } = clampSizeToCellBounds(b.x, b.y, requested, requested, usableWidthIn, usableHeightIn);
            return { ...b, size: Math.min(width, height) };
          })
        );
      }
    };
    const handleUp = () => {
      setActiveId(null);
      setResizingId(null);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [activeId, resizingId, editable, setBlocks, usableWidthIn, usableHeightIn]);

  return (
    <div ref={containerRef} className="ss-print__qr-layer">
      {blocks.map((b) => {
        // Defensive clamp at render time too — not just during drag/resize
        // — same rationale as TextBlockLayer/ImageBlockLayer: a stale
        // stored position/size can never render outside the cell border.
        const { x: renderX, y: renderY } = clampToCellBounds(b.x, b.y, b.size, b.size, usableWidthIn, usableHeightIn);
        const { width: clampedWidth, height: clampedHeight } = clampSizeToCellBounds(
          renderX,
          renderY,
          b.size,
          b.size,
          usableWidthIn,
          usableHeightIn
        );
        const renderSize = Math.min(clampedWidth, clampedHeight);
        const leftPct = usableWidthIn > 0 ? (renderX / usableWidthIn) * 100 : 0;
        const topPct = usableHeightIn > 0 ? (renderY / usableHeightIn) * 100 : 0;
        const widthPct = usableWidthIn > 0 ? (renderSize / usableWidthIn) * 100 : 0;
        const heightPct = usableHeightIn > 0 ? (renderSize / usableHeightIn) * 100 : 0;

        return (
          <div
            key={b.id}
            className={[
              'ss-print__qr-block',
              editable ? 'is-editable' : '',
              selectedIds.includes(b.id) ? 'is-selected' : '',
              showBoundaries ? 'show-boundaries' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              left: `${leftPct}%`,
              top: `${topPct}%`,
              width: `${widthPct}%`,
              height: `${heightPct}%`,
            }}
            onMouseDown={(e) => startDrag(e, b.id)}
          >
            <QRCodeSVG
              value={b.value || ' '}
              className="ss-print__qr-code"
              level={b.ecLevel}
              fgColor={b.fgColor}
              bgColor={b.bgColor}
              marginSize={b.marginModules}
            />
            {editable && <div className="ss-print__qr-resize-handle" onMouseDown={(e) => startResize(e, b.id)} />}
          </div>
        );
      })}
    </div>
  );
}
